import Cookies, { CookieAttributes } from 'js-cookie'

export type AuthToken = 'access' | 'refresh'

export const getTokenCookieName = (type: AuthToken): string => {
    switch (type) {
        case 'access':
            return `optimo_access_token`
        case 'refresh':
            return `optimo_refresh_token`
        default:
            throw new Error('Invalid token type')
    }
}

const getCookieOptions = (includeDefaults = true): CookieAttributes => {
    const cookieDomain = import.meta.env.VITE_COOKIE_DOMAIN as string | undefined
    const cookieSecureEnv = (import.meta.env.VITE_COOKIE_SECURE as string | undefined) || 'auto'
    const cookieOptions: CookieAttributes = {}

    // Add default options for setting cookies (matching Diversio-Frontend pattern)
    if (includeDefaults) {
        // Determine secure flag:
        // - 'true'  => always secure
        // - 'false' => never secure (useful for local http dev)
        // - 'auto'  => secure only when served over https
        if (cookieSecureEnv === 'true') {
            cookieOptions.secure = true
        } else if (cookieSecureEnv === 'false') {
            cookieOptions.secure = false
        } else {
            cookieOptions.secure = typeof window !== 'undefined' && window.location.protocol === 'https:'
        }
        cookieOptions.sameSite = 'Lax'
    }

    // Only set domain if explicitly configured to avoid cross-subdomain issues
    if (cookieDomain && cookieDomain !== 'auto') {
        cookieOptions.domain = cookieDomain
    }

    return cookieOptions
}

export const setAuthToken = (token: string, type: AuthToken) => {
    const cookieOptions = getCookieOptions(true)

    // Set different expiration times based on token type
    if (type === 'access') {
        // Access tokens expire in 1 day
        cookieOptions.expires = 1
    } else if (type === 'refresh') {
        // Refresh tokens expire in 7 days
        cookieOptions.expires = 7
    }

    Cookies.set(getTokenCookieName(type), token, cookieOptions)
}

export const getAuthToken = (type: AuthToken) => {
    return Cookies.get(getTokenCookieName(type))
}

export const removeAuthToken = (type: AuthToken) => {
    const cookieOptions = getCookieOptions(false)
    Cookies.remove(getTokenCookieName(type), cookieOptions)
}

export const clearAllTokens = () => {
    removeAuthToken('access')
    removeAuthToken('refresh')
}

export const hasValidTokens = (): boolean => {
    return !!(getAuthToken('access') && getAuthToken('refresh'))
}
